import { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../config";

const ITEMS_API = `${API_BASE_URL}api/Marketing_items`;
const GR_API = `${API_BASE_URL}api/GR_Marketing`;

const generateNumber = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const emptyForm = () => ({
  number: generateNumber(),
  series: "Primary",
  priceList: "Last Evaluated Price",
  postingDate: new Date().toISOString().split("T")[0],
  documentDate: new Date().toISOString().split("T")[0],
  ref2: "",
  remarks: "",
  journalRemark: "Goods Receipt",
  items: [{ itemNo: "", itemDescription: "", quantity: "", accountCode: "", unitPrice: "", whse: "", expenseCategory: "", addtlInfo: "" }],
});

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "2-digit" }) : "—";

// ─── Search Icon ──────────────────────────────────────────────────────────────
const SearchIcon = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.55)", backdropFilter: "blur(5px)" }} onClick={onCancel} />
      <div style={{ position: "relative", zIndex: 10, background: "#fff", borderRadius: 16, width: "min(420px,92vw)", boxShadow: "0 24px 64px rgba(0,0,0,.22)", fontFamily: '"Outfit",sans-serif', overflow: "hidden" }}>
        <div style={{ background: danger ? "linear-gradient(135deg,#ef4444,#b91c1c)" : "linear-gradient(135deg,#185FA5,#0C447C)", padding: ".875rem 1.25rem", display: "flex", alignItems: "center", gap: 10 }}>
          {danger
            ? <svg width={18} height={18} fill="none" stroke="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
            : <svg width={18} height={18} fill="none" stroke="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
          <span style={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>{title}</span>
        </div>
        <div style={{ padding: "1.25rem 1.5rem" }}>
          <p style={{ color: "#334155", fontSize: ".875rem", lineHeight: 1.6, margin: 0 }}>{message}</p>
        </div>
        <div style={{ padding: ".875rem 1.5rem", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel}
            style={{ padding: ".5rem 1.25rem", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: ".875rem", fontWeight: 600, color: "#475569", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ padding: ".5rem 1.25rem", border: "none", borderRadius: 8, fontSize: ".875rem", fontWeight: 700, color: "#fff", background: danger ? "linear-gradient(135deg,#ef4444,#b91c1c)" : "#1D9E75", cursor: "pointer", fontFamily: "inherit" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Item Lookup Modal ────────────────────────────────────────────────────────
function ItemLookupModal({ open, onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ITEMS_API);
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) { setSearch(""); fetchItems(); } }, [open, fetchItems]);

  const filtered = items.filter(i =>
    i.isActive === 1 && (
      i.itemCode?.toLowerCase().includes(search.toLowerCase()) ||
      i.description?.toLowerCase().includes(search.toLowerCase())
    )
  );

  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.55)", backdropFilter: "blur(5px)" }} onClick={onClose} />
      <div style={{ position: "relative", zIndex: 10, background: "#fff", borderRadius: 16, width: "min(620px,94vw)", maxHeight: "78vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.22)", overflow: "hidden", fontFamily: '"Outfit",sans-serif' }}>
        <div style={{ background: "linear-gradient(135deg,#185FA5,#0C447C)", padding: ".875rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SearchIcon size={15} />
            <span style={{ color: "#fff", fontWeight: 700, fontSize: ".9rem" }}>Item Lookup</span>
            <span style={{ background: "rgba(255,255,255,.18)", color: "#bfdbfe", fontSize: ".7rem", fontWeight: 600, padding: "2px 10px", borderRadius: 99, border: "1px solid rgba(255,255,255,.2)" }}>Marketing Items</span>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 6, padding: ".3rem", cursor: "pointer", color: "#fff", display: "flex" }}>
            <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{ padding: ".75rem 1rem", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}><SearchIcon size={13} /></span>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search item code or description…"
              style={{ width: "100%", paddingLeft: 32, paddingRight: 12, height: 34, border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".85rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <p style={{ fontSize: ".7rem", color: "#94a3b8", marginTop: 5, marginLeft: 2 }}>{filtered.length} item{filtered.length !== 1 ? "s" : ""} — click to select</p>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2.5rem", gap: 8, color: "#94a3b8" }}>
              <svg style={{ animation: "gr-spin .7s linear infinite", width: 18, height: 18 }} fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
              </svg>
              <span style={{ fontSize: ".85rem" }}>Loading…</span>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".84rem" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {["Item Code", "Description", "Avail. Qty", "Unit"].map(h => (
                    <th key={h} style={{ padding: ".5rem 1rem", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: "2.5rem", textAlign: "center", color: "#94a3b8" }}>No items found.</td></tr>
                ) : filtered.map(item => (
                  <tr key={item.id} onClick={() => onSelect(item)}
                    style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: ".5rem 1rem", fontFamily: "monospace", fontWeight: 700, color: "#185FA5", fontSize: ".8rem" }}>{item.itemCode}</td>
                    <td style={{ padding: ".5rem 1rem", color: "#334155" }}>{item.description || <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                    <td style={{ padding: ".5rem 1rem", fontWeight: 700, color: item.availableQty > 0 ? "#1D9E75" : "#E24B4A", fontSize: ".8rem" }}>{Number(item.availableQty || 0).toLocaleString()}</td>
                    <td style={{ padding: ".5rem 1rem" }}>
                      {item.unit ? <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 7px", borderRadius: 4, fontSize: ".72rem", fontFamily: "monospace", fontWeight: 600 }}>{item.unit}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ borderTop: "1px solid #f1f5f9", padding: ".625rem 1rem", background: "#f8fafc", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: ".375rem .875rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: ".8rem", fontWeight: 600, color: "#475569", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── GR Form (Create & Edit) ──────────────────────────────────────────────────
function GRForm({ editData, onClose, onSaved }) {
  const isEdit = !!editData;
  const [form, setForm] = useState(() => {
    if (isEdit) return {
      ...emptyForm(),
      ...editData,
      items: editData.items?.length > 0 ? editData.items.map(i => ({
        itemNo: i.itemNo || "",
        itemDescription: i.itemDescription || "",
        quantity: i.quantity ?? "",
        accountCode: i.accountCode || "",
        unitPrice: i.unitPrice ?? "",
        whse: i.whse || "",
        expenseCategory: i.expenseCategory || "",
        addtlInfo: i.addtlInfo || "",
      })) : emptyForm().items,
    };
    return emptyForm();
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);

  const handleField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const handleItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm(f => ({ ...f, items }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { itemNo: "", itemDescription: "", quantity: "", accountCode: "", unitPrice: "", whse: "", expenseCategory: "", addtlInfo: "" }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const openLookup = (idx) => { setActiveRowIdx(idx); setLookupOpen(true); };
  const handleSelectItem = (sel) => {
    if (activeRowIdx === null) return;
    const items = [...form.items];
    items[activeRowIdx] = { ...items[activeRowIdx], itemNo: sel.itemCode || "", itemDescription: sel.description || "" };
    setForm(f => ({ ...f, items }));
    setLookupOpen(false);
    setActiveRowIdx(null);
  };

  const handleSubmit = async () => {
    setLoading(true); setError(null);
    try {
      const payload = {
        number: form.number,
        series: form.series,
        priceList: form.priceList || null,
        postingDate: form.postingDate,
        documentDate: form.documentDate,
        ref2: form.ref2 || null,
        remarks: form.remarks || null,
        journalRemark: form.journalRemark || "Goods Receipt",
        items: form.items.map(i => ({
          itemNo: i.itemNo,
          itemDescription: i.itemDescription || null,
          quantity: Number(i.quantity) || 0,
          accountCode: i.accountCode || null,
          unitPrice: Number(i.unitPrice) || 0,
          whse: i.whse || null,
          expenseCategory: i.expenseCategory || null,
          addtlInfo: i.addtlInfo || null,
        })),
      };

      const method = isEdit ? "PUT" : "POST";
      const url = isEdit ? `${GR_API}/${editData.id}` : GR_API;
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Server error ${res.status}`);
      }
      onSaved(isEdit ? "updated" : "created");
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ItemLookupModal open={lookupOpen} onClose={() => { setLookupOpen(false); setActiveRowIdx(null); }} onSelect={handleSelectItem} />

      <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", background: "rgba(15,23,42,.5)", backdropFilter: "blur(6px)", padding: "1.5rem 1rem" }}>
        <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 1280, boxShadow: "0 24px 64px rgba(0,0,0,.2)", fontFamily: '"Outfit",sans-serif', margin: "auto" }}>

          <div style={{ background: "linear-gradient(135deg,#185FA5,#0C447C)", padding: "1rem 1.5rem", borderRadius: "18px 18px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width={20} height={20} fill="none" stroke="#fff" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>{isEdit ? "Edit Goods Receipt" : "New Goods Receipt"}</span>
              {isEdit && <span style={{ background: "rgba(255,255,255,.2)", color: "#bfdbfe", fontSize: ".72rem", fontWeight: 600, padding: "2px 10px", borderRadius: 99 }}>#{editData.number}</span>}
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 8, padding: ".4rem", cursor: "pointer", color: "#fff", display: "flex" }}>
              <svg width={18} height={18} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {error && (
            <div style={{ margin: "1rem 1.5rem 0", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: ".75rem 1rem", borderRadius: 10, fontSize: ".875rem", display: "flex", alignItems: "center", gap: 8 }}>
              <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <span>{error}</span>
              <button onClick={() => setError(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#b91c1c" }}>✕</button>
            </div>
          )}

          <div style={{ padding: "1.25rem 1.5rem", display: "flex", gap: "3rem", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: ".75rem", flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <label style={{ fontSize: ".7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", minWidth: 65 }}>Number</label>
                <input value={form.number} readOnly style={{ width: 155, padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".83rem", fontFamily: "monospace", background: "#f8fafc", color: "#94a3b8", outline: "none" }} />
                <label style={{ fontSize: ".7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>Series</label>
                <select value={form.series} onChange={e => handleField("series", e.target.value)}
                  style={{ padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".875rem", outline: "none", fontFamily: "inherit", width: 130 }}>
                  <option>Primary</option><option>Secondary</option>
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <label style={{ fontSize: ".7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", minWidth: 65 }}>Price List</label>
                <select value={form.priceList} onChange={e => handleField("priceList", e.target.value)}
                  style={{ padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".875rem", outline: "none", fontFamily: "inherit", width: 220 }}>
                  <option>Last Evaluated Price</option><option>Standard Price</option><option>Sales Price</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".625rem", minWidth: 230 }}>
              {[
                { label: "Posting Date", field: "postingDate", type: "date" },
                { label: "Document Date", field: "documentDate", type: "date" },
                { label: "Ref. 2", field: "ref2", placeholder: "—" },
              ].map(({ label, field, type, placeholder }) => (
                <div key={field} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <label style={{ fontSize: ".7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap" }}>{label}</label>
                  <input type={type || "text"} value={form[field]} onChange={e => handleField(field, e.target.value)} placeholder={placeholder}
                    style={{ padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".875rem", outline: "none", fontFamily: "inherit", width: 145 }} />
                </div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: "1px solid #f1f5f9" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    {["#","(01) Item No.","(02) Item Description","(03) Qty","(06) Account Code","Unit Price","(04) Whse","(05) Expense Category","Addtl Info",""].map((h, i) => (
                      <th key={i} style={{ padding: ".7rem .875rem", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: ".68rem", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f8fafc" }}
                      onMouseEnter={e => { [...e.currentTarget.cells].forEach(c => c.style.background = "#f0f7ff"); }}
                      onMouseLeave={e => { [...e.currentTarget.cells].forEach(c => c.style.background = ""); }}>
                      <td style={{ padding: ".4rem .875rem", color: "#cbd5e1", fontFamily: "monospace", fontSize: ".75rem" }}>{idx + 1}</td>
                      <td style={{ padding: ".35rem .45rem" }}>
                        <div style={{ position: "relative" }}>
                          <input value={item.itemNo} onChange={e => handleItem(idx, "itemNo", e.target.value)} placeholder="e.g. MK0013"
                            style={{ width: "100%", padding: ".38rem 1.6rem .38rem .55rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: ".8rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                          <button type="button" onClick={() => openLookup(idx)}
                            style={{ position: "absolute", right: ".35rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 0 }}
                            onMouseEnter={e => e.currentTarget.style.color = "#185FA5"}
                            onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}>
                            <SearchIcon size={12} />
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: ".35rem .45rem" }}>
                        <div style={{ position: "relative" }}>
                          <input value={item.itemDescription} onChange={e => handleItem(idx, "itemDescription", e.target.value)} placeholder="Description"
                            style={{ width: "100%", padding: ".38rem 1.6rem .38rem .55rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: ".8rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                          <button type="button" onClick={() => openLookup(idx)}
                            style={{ position: "absolute", right: ".35rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 0 }}
                            onMouseEnter={e => e.currentTarget.style.color = "#185FA5"}
                            onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}>
                            <SearchIcon size={12} />
                          </button>
                        </div>
                      </td>
                      {[
                        { f: "quantity", type: "number", ph: "0" },
                        { f: "accountCode", ph: "101050500" },
                        { f: "unitPrice", type: "number", ph: "0.00" },
                        { f: "whse", ph: "MSW-001" },
                        { f: "expenseCategory", ph: "—" },
                        { f: "addtlInfo", ph: "—" },
                      ].map(({ f, type, ph }) => (
                        <td key={f} style={{ padding: ".35rem .45rem" }}>
                          <input type={type || "text"} value={item[f] || ""} onChange={e => handleItem(idx, f, e.target.value)} placeholder={ph}
                            style={{ width: "100%", padding: ".38rem .55rem", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: ".8rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                        </td>
                      ))}
                      <td style={{ padding: ".35rem .45rem" }}>
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(idx)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", display: "flex", padding: ".2rem", borderRadius: 4 }}
                            onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                            onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>
                            <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {Array.from({ length: Math.max(0, 4 - form.items.length) }).map((_, i) => (
                    <tr key={`ef${i}`} style={{ height: 36, borderBottom: "1px solid #f8fafc" }}>
                      {Array.from({ length: 10 }).map((_, j) => <td key={j}></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: ".625rem 1rem", borderTop: "1px solid #f1f5f9" }}>
              <button onClick={addItem}
                style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: "#185FA5", fontWeight: 600, fontSize: ".85rem", fontFamily: "inherit", padding: ".3rem .5rem", borderRadius: 6 }}
                onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Row
              </button>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #e2e8f0", padding: "1.125rem 1.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
            <div>
              <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".5rem" }}>Remarks</label>
              <textarea value={form.remarks} onChange={e => handleField("remarks", e.target.value)} rows={2} placeholder="Enter remarks…"
                style={{ width: "100%", padding: ".625rem .875rem", border: "1px solid #e2e8f0", borderRadius: 9, fontSize: ".875rem", outline: "none", fontFamily: "inherit", resize: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: ".7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".5rem" }}>Journal Remark</label>
              <input value={form.journalRemark} onChange={e => handleField("journalRemark", e.target.value)}
                style={{ width: "100%", padding: ".625rem .875rem", border: "1px solid #e2e8f0", borderRadius: 9, fontSize: ".875rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
          </div>

          <div style={{ borderTop: "1px solid #e2e8f0", padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: ".75rem" }}>
            <button onClick={handleSubmit} disabled={loading}
              style={{ display: "flex", alignItems: "center", gap: 6, background: loading ? "#94a3b8" : "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", border: "none", borderRadius: 9, padding: ".625rem 1.5rem", fontWeight: 700, fontSize: ".875rem", cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {loading ? (
                <>
                  <svg style={{ animation: "gr-spin .7s linear infinite", width: 14, height: 14 }} fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
                  </svg>
                  Saving…
                </>
              ) : isEdit ? "Update" : "Add"}
            </button>
            <button onClick={onClose}
              style={{ padding: ".625rem 1.25rem", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, fontWeight: 600, fontSize: ".875rem", cursor: "pointer", color: "#475569", fontFamily: "inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main List Page ───────────────────────────────────────────────────────────
export default function GR_Marketing_LIST() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [posting, setPosting] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(GR_API, { headers: { Accept: "*/*" } });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setRecords(await res.json());
    } catch (err) { setError(err.message || "Failed to load."); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handlePost = (record, e) => {
    e?.stopPropagation();
    if (record.isPost) return;
    setConfirm({ type: "post", record });
  };

  const doPost = async (record) => {
    setConfirm(null);
    const itemsToAdd = (record.items || [])
      .filter(i => i.itemNo && Number(i.quantity) > 0)
      .map(i => ({ itemCode: i.itemNo, quantity: Number(i.quantity) }));

    if (itemsToAdd.length === 0) { showToast("No valid items to post.", "error"); return; }

    setPosting(record.id);
    try {
      const res = await fetch(`${ITEMS_API}/add-stock`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(itemsToAdd),
      });
      if (!res.ok) throw new Error(`Post failed ${res.status}`);

      await fetch(`${GR_API}/${record.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify({ isPost: true }),
      });

      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, isPost: true } : r));
      showToast(`GR #${record.number} posted — stock updated!`);
    } catch (err) {
      showToast(err.message || "Post failed.", "error");
    } finally {
      setPosting(null);
    }
  };

  const handleDelete = (id, e) => {
    e?.stopPropagation();
    setConfirm({ type: "delete", id });
  };

  const doDelete = async (id) => {
    setConfirm(null);
    setDeleting(id);
    try {
      const res = await fetch(`${GR_API}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed ${res.status}`);
      setRecords(prev => prev.filter(r => r.id !== id));
      if (selectedId === id) setSelectedId(null);
      showToast("Goods Receipt deleted.");
    } catch (err) { showToast(err.message, "error"); }
    setDeleting(null);
  };

  const openNew = () => { setEditData(null); setShowForm(true); };
  const openEdit = (rec, e) => { e?.stopPropagation(); setEditData(rec); setShowForm(true); };

  const handleSaved = (action) => {
    setShowForm(false);
    setEditData(null);
    fetchAll();
    showToast(action === "updated" ? "GR updated successfully!" : "GR created! Click Post to update stock.");
  };

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    return (
      String(r.number).includes(q) ||
      (r.ref2 || "").toLowerCase().includes(q) ||
      (r.journalRemark || "").toLowerCase().includes(q) ||
      (r.postingDate || "").includes(q) ||
      (r.series || "").toLowerCase().includes(q)
    );
  });

  const selected = records.find(r => r.id === selectedId);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');
        @keyframes gr-spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes gr-toast { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .gr-list-row { cursor:pointer; transition:background .1s; }
        .gr-list-row:hover td { background:#f8faff !important; }
        .gr-list-row.selected td { background:#eff6ff !important; }
      `}</style>

      {/* ── Confirm Modal ── */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.type === "delete" ? "Delete Goods Receipt" : `Post GR #${confirm?.record?.number}`}
        message={confirm?.type === "delete"
          ? "Are you sure you want to delete this Goods Receipt? This cannot be undone."
          : "This will add the items to Marketing stock. Continue?"
        }
        confirmLabel={confirm?.type === "delete" ? "Delete" : "Post"}
        danger={confirm?.type === "delete"}
        onConfirm={() => confirm?.type === "delete" ? doDelete(confirm.id) : doPost(confirm.record)}
        onCancel={() => setConfirm(null)}
      />

      {showForm && (
        <GRForm
          editData={editData}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={handleSaved}
        />
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 300, animation: "gr-toast .3s ease", background: toast.type === "error" ? "#fef2f2" : "#d1fae5", border: `1px solid ${toast.type === "error" ? "#fecaca" : "#a7f3d0"}`, color: toast.type === "error" ? "#b91c1c" : "#065f46", padding: ".75rem 1.25rem", borderRadius: 12, fontSize: ".875rem", fontWeight: 600, fontFamily: '"Outfit",sans-serif', boxShadow: "0 4px 16px rgba(0,0,0,.12)", display: "flex", alignItems: "center", gap: 8 }}>
          {toast.type === "error"
            ? <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            : <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          }
          {toast.msg}
        </div>
      )}

      <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: '"Outfit",sans-serif', padding: "2rem 1.5rem" }}>
        <div style={{ maxWidth: 1700, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>

          <div style={{ background: "linear-gradient(135deg,#185FA5,#0C447C)", borderRadius: 16, padding: "1.25rem 1.75rem", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 4px 16px rgba(24,95,165,.25)" }}>
            <div>
              <h1 style={{ color: "#fff", fontSize: "1.5rem", fontWeight: 700, margin: 0, letterSpacing: "-.02em" }}>Goods Receipt List</h1>
              <p style={{ color: "#bfdbfe", fontSize: ".85rem", margin: "2px 0 0" }}>Marketing — Mega Soft Hygienic Products, Inc.</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={fetchAll} disabled={loading}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.25)", color: "#fff", borderRadius: 9, padding: ".5rem 1rem", fontWeight: 600, fontSize: ".85rem", cursor: "pointer", fontFamily: "inherit" }}>
                <svg style={{ animation: loading ? "gr-spin .8s linear infinite" : "none", width: 14, height: 14 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <button onClick={openNew}
                style={{ display: "flex", alignItems: "center", gap: 6, background: "#1D9E75", border: "none", color: "#fff", borderRadius: 9, padding: ".5rem 1.25rem", fontWeight: 700, fontSize: ".85rem", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(29,158,117,.35)" }}>
                <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                New GR
              </button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 380 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}><SearchIcon size={14} /></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by number, ref, date…"
                style={{ width: "100%", paddingLeft: 34, paddingRight: 12, height: 38, border: "1px solid #e2e8f0", borderRadius: 9, fontSize: ".875rem", background: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, padding: ".5rem 1rem", fontSize: ".85rem", color: "#64748b", fontWeight: 600 }}>
              Total: <span style={{ color: "#185FA5" }}>{filtered.length}</span> records
            </div>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: ".75rem 1rem", borderRadius: 10, fontSize: ".875rem", display: "flex", alignItems: "center", gap: 8 }}>
              <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
              <button onClick={fetchAll} style={{ marginLeft: "auto", fontWeight: 600, fontSize: ".8rem", textDecoration: "underline", background: "none", border: "none", cursor: "pointer", color: "#b91c1c" }}>Retry</button>
            </div>
          )}

          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ flex: 1, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
              {loading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "5rem", gap: 10, color: "#94a3b8" }}>
                  <svg style={{ animation: "gr-spin .8s linear infinite", width: 22, height: 22 }} fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
                  </svg>
                  <span style={{ fontSize: ".9rem" }}>Loading records…</span>
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "5rem", color: "#94a3b8" }}>
                  <svg width={42} height={42} fill="none" stroke="#cbd5e1" viewBox="0 0 24 24" style={{ marginBottom: 10 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p style={{ fontSize: ".9rem" }}>{search ? "No records match your search." : "No Goods Receipts yet. Click New GR to start."}</p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                        {["#","GR Number","Series","Price List","Posting Date","Doc Date","Ref. 2","Journal Remark","Items","Status","Actions"].map(h => (
                          <th key={h} style={{ padding: ".75rem 1rem", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, idx) => (
                        <tr key={r.id} className={`gr-list-row${selectedId === r.id ? " selected" : ""}`}
                          onClick={() => setSelectedId(selectedId === r.id ? null : r.id)}
                          style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: ".75rem 1rem", color: "#cbd5e1", fontFamily: "monospace", fontSize: ".75rem" }}>{idx + 1}</td>
                          <td style={{ padding: ".75rem 1rem" }}><span style={{ fontWeight: 700, color: "#185FA5" }}>#{r.number}</span></td>
                          <td style={{ padding: ".75rem 1rem", color: "#475569", fontSize: ".85rem" }}>{r.series}</td>
                          <td style={{ padding: ".75rem 1rem", color: "#64748b", fontSize: ".82rem" }}>{r.priceList || "—"}</td>
                          <td style={{ padding: ".75rem 1rem", color: "#64748b", fontSize: ".82rem", whiteSpace: "nowrap" }}>{fmtDate(r.postingDate)}</td>
                          <td style={{ padding: ".75rem 1rem", color: "#64748b", fontSize: ".82rem", whiteSpace: "nowrap" }}>{fmtDate(r.documentDate)}</td>
                          <td style={{ padding: ".75rem 1rem", color: "#64748b", fontSize: ".82rem" }}>{r.ref2 || "—"}</td>
                          <td style={{ padding: ".75rem 1rem", color: "#64748b", fontSize: ".82rem" }}>{r.journalRemark || "—"}</td>
                          <td style={{ padding: ".75rem 1rem" }}>
                            <span style={{ background: "#dbeafe", color: "#1e40af", fontSize: ".72rem", fontWeight: 700, padding: "3px 9px", borderRadius: 99 }}>{r.items?.length || 0} items</span>
                          </td>
                          <td style={{ padding: ".75rem 1rem" }}>
                            {r.isPost
                              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#d1fae5", color: "#065f46", fontSize: ".72rem", fontWeight: 700, padding: "3px 9px", borderRadius: 99, border: "1px solid #a7f3d0" }}>
                                  <svg width={10} height={10} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                  Posted
                                </span>
                              : <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fef9c3", color: "#854d0e", fontSize: ".72rem", fontWeight: 700, padding: "3px 9px", borderRadius: 99, border: "1px solid #fde68a" }}>
                                  <svg width={10} height={10} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" /></svg>
                                  Unposted
                                </span>
                            }
                          </td>
                          <td style={{ padding: ".75rem 1rem" }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              {r.isPost ? (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: ".35rem .7rem", borderRadius: 6, background: "#f1f5f9", color: "#94a3b8", fontSize: ".75rem", fontWeight: 700, border: "1px solid #e2e8f0", cursor: "not-allowed", userSelect: "none" }}>
                                  <svg width={13} height={13} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                                  Posted
                                </span>
                              ) : (
                                <button onClick={e => handlePost(r, e)} disabled={posting === r.id} title="Post items to stock"
                                  style={{ display: "flex", alignItems: "center", gap: 4, padding: ".35rem .7rem", border: "1px solid transparent", borderRadius: 6, background: "transparent", cursor: posting === r.id ? "not-allowed" : "pointer", color: "#64748b", fontSize: ".75rem", fontWeight: 700, fontFamily: "inherit", opacity: posting === r.id ? .6 : 1, transition: "all .15s" }}
                                  onMouseEnter={e => { if (posting !== r.id) { e.currentTarget.style.background = "#d1fae5"; e.currentTarget.style.color = "#065f46"; e.currentTarget.style.borderColor = "#a7f3d0"; }}}
                                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748b"; e.currentTarget.style.borderColor = "transparent"; }}>
                                  {posting === r.id ? (
                                    <svg style={{ animation: "gr-spin .7s linear infinite", width: 13, height: 13 }} fill="none" viewBox="0 0 24 24">
                                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                                      <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
                                    </svg>
                                  ) : (
                                    <svg width={13} height={13} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  )}
                                  Post
                                </button>
                              )}
                              <button onClick={e => openEdit(r, e)}
                                style={{ display: "flex", alignItems: "center", gap: 4, padding: ".35rem .7rem", border: "1px solid transparent", borderRadius: 6, background: "transparent", cursor: "pointer", color: "#64748b", fontSize: ".75rem", fontWeight: 600, fontFamily: "inherit", transition: "all .15s" }}
                                onMouseEnter={e => { e.currentTarget.style.background = "#eff6ff"; e.currentTarget.style.color = "#185FA5"; e.currentTarget.style.borderColor = "#bfdbfe"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748b"; e.currentTarget.style.borderColor = "transparent"; }}>
                                <svg width={13} height={13} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                Edit
                              </button>
                              <button onClick={e => handleDelete(r.id, e)} disabled={deleting === r.id}
                                style={{ display: "flex", alignItems: "center", gap: 4, padding: ".35rem .7rem", border: "1px solid transparent", borderRadius: 6, background: "transparent", cursor: deleting === r.id ? "not-allowed" : "pointer", color: "#64748b", fontSize: ".75rem", fontWeight: 600, fontFamily: "inherit", opacity: deleting === r.id ? .5 : 1, transition: "all .15s" }}
                                onMouseEnter={e => { if (deleting !== r.id) { e.currentTarget.style.background = "#fef2f2"; e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.borderColor = "#fecaca"; }}}
                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748b"; e.currentTarget.style.borderColor = "transparent"; }}>
                                {deleting === r.id
                                  ? <svg style={{ animation: "gr-spin .7s linear infinite", width: 13, height: 13 }} fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} /><path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} /></svg>
                                  : <svg width={13} height={13} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                }
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Detail Panel */}
            {selected && (
              <div style={{ width: 300, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", padding: "1.25rem", boxShadow: "0 1px 4px rgba(0,0,0,.06)", flexShrink: 0, position: "sticky", top: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: ".9rem" }}>GR #{selected.number}</span>
                  <button onClick={() => setSelectedId(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: ".2rem", borderRadius: 4 }}
                    onMouseEnter={e => e.currentTarget.style.color = "#475569"}
                    onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}>
                    <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", marginBottom: "1rem" }}>
                  {[
                    { label: "Series", value: selected.series },
                    { label: "Price List", value: selected.priceList || "—" },
                    { label: "Posting Date", value: fmtDate(selected.postingDate) },
                    { label: "Document Date", value: fmtDate(selected.documentDate) },
                    { label: "Ref. 2", value: selected.ref2 || "—" },
                    { label: "Journal", value: selected.journalRemark || "—" },
                    { label: "Remarks", value: selected.remarks || "—" },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: ".78rem" }}>
                      <span style={{ color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", fontSize: ".68rem", letterSpacing: ".04em" }}>{label}</span>
                      <span style={{ color: "#334155", textAlign: "right", fontWeight: 500 }}>{value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: ".875rem" }}>
                  <p style={{ fontSize: ".68rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".625rem" }}>
                    Items ({selected.items?.length || 0})
                  </p>
                  <div style={{ maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                    {(selected.items || []).map((item, i) => (
                      <div key={i} style={{ background: "#f8fafc", borderRadius: 8, padding: ".625rem .75rem", border: "1px solid #f1f5f9" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                          <span style={{ fontWeight: 700, color: "#185FA5", fontFamily: "monospace", fontSize: ".78rem" }}>{item.itemNo}</span>
                          <span style={{ fontSize: ".75rem", color: "#64748b", fontWeight: 600 }}>Qty: {item.quantity}</span>
                        </div>
                        <div style={{ fontSize: ".75rem", color: "#475569" }}>{item.itemDescription || "—"}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: ".72rem", color: "#94a3b8" }}>
                          <span>Whse: {item.whse || "—"}</span>
                          <span>₱{Number(item.unitPrice || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: ".875rem", marginTop: ".875rem", display: "flex", gap: 8, flexDirection: "column" }}>
                  {selected.isPost ? (
                    <div style={{ width: "100%", background: "#f1f5f9", color: "#94a3b8", border: "1px solid #e2e8f0", borderRadius: 8, padding: ".5rem", fontWeight: 700, fontSize: ".8rem", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "not-allowed" }}>
                      <svg width={13} height={13} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      Already Posted
                    </div>
                  ) : (
                    <button onClick={e => handlePost(selected, e)} disabled={posting === selected.id}
                      style={{ width: "100%", background: posting === selected.id ? "#94a3b8" : "#1D9E75", color: "#fff", border: "none", borderRadius: 8, padding: ".5rem", fontWeight: 700, fontSize: ".8rem", cursor: posting === selected.id ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      {posting === selected.id ? (
                        <>
                          <svg style={{ animation: "gr-spin .7s linear infinite", width: 13, height: 13 }} fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                            <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
                          </svg>
                          Posting…
                        </>
                      ) : (
                        <>
                          <svg width={13} height={13} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                          Post to Stock
                        </>
                      )}
                    </button>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={e => openEdit(selected, e)}
                      style={{ flex: 1, background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, padding: ".5rem", fontWeight: 600, fontSize: ".8rem", cursor: "pointer", fontFamily: "inherit" }}>
                      Edit
                    </button>
                    <button onClick={e => handleDelete(selected.id, e)}
                      style={{ flex: 1, background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 8, padding: ".5rem", fontWeight: 600, fontSize: ".8rem", cursor: "pointer", fontFamily: "inherit" }}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}